import { Box, Divider } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';

import { IPrices, IVendorConfig } from '@rbi-ctg/menu';
import { pluIsAvailable } from 'utils/availability/menu-availability';
import { centsToDollarString } from 'utils/index';
import { PluTypes, concatenateSizePlu } from 'utils/vendor-config';

import { CopyButton } from './copy-button';

const PRICE_NOT_AVAILABLE = 'Not Available';

const PriceRow = Box.withConfig({
  flexDirection: 'row',
  gap: '$4',
  justifyContent: 'space-between',
});

const PriceInfo = ({
  price,
  pluIsAvailable,
}: {
  price: number | undefined;
  pluIsAvailable: boolean;
}) => {
  const priceToString = centsToDollarString(price);
  const priceToCopy = pluIsAvailable ? priceToString : PRICE_NOT_AVAILABLE;
  const priceLabel = pluIsAvailable ? `Price: $${priceToString}` : `Price: ${PRICE_NOT_AVAILABLE}`;
  return <CopyButton label={priceLabel} textToCopy={priceToCopy} variant="copyOne" />;
};

const PluInfoDetails = ({
  vendorConfig,
  prices,
  hidePrices = false,
}: {
  vendorConfig: IVendorConfig;
  prices: IPrices;
  hidePrices?: boolean;
}) => {
  switch (vendorConfig.pluType) {
    case PluTypes.CONSTANT:
      const constantPluIsAvailable = pluIsAvailable(vendorConfig.constantPlu, prices);
      return (
        <PriceRow>
          <CopyButton
            label={`${vendorConfig.pluType}: ${vendorConfig.constantPlu}`}
            textToCopy={vendorConfig.constantPlu}
            variant="copyOne"
          />
          {!hidePrices ? (
            <PriceInfo
              price={prices?.[vendorConfig.constantPlu]}
              pluIsAvailable={constantPluIsAvailable}
            />
          ) : null}
        </PriceRow>
      );
    case PluTypes.IGNORE:
      return <Box>{`PLU: ignore`}</Box>;
    case PluTypes.MULTI_CONSTANT:
      let pluIsAvailableInPrices: boolean = false;
      return (
        <>
          {(vendorConfig.multiConstantPlus || []).map(({ plu }) => {
            pluIsAvailableInPrices = pluIsAvailable(plu, prices);
            return (
              <>
                <CopyButton key={plu} label={`PLU: ${plu}`} textToCopy={plu} />
                {!hidePrices ? (
                  <PriceInfo price={prices?.[plu]} pluIsAvailable={pluIsAvailableInPrices} />
                ) : null}
                <Divider />
              </>
            );
          })}
        </>
      );
    case PluTypes.PARENT_CHILD:
      const { plu: parentPlu, childPlu } = vendorConfig.parentChildPlu;
      const parentChildPlu = `${parentPlu}-${childPlu}`;

      const parentPluIsAvailable = pluIsAvailable(parentPlu, prices);
      const childPluIsAvailable = pluIsAvailable(childPlu, prices);
      const parentChildPluIsAvailable = pluIsAvailable(parentChildPlu, prices);

      return (
        <>
          <PriceRow>
            <CopyButton label={`Parent PLU: ${parentPlu}`} textToCopy={parentPlu} />
            {!hidePrices ? (
              <PriceInfo price={prices?.[parentPlu]} pluIsAvailable={parentPluIsAvailable} />
            ) : null}
          </PriceRow>
          <PriceRow>
            <CopyButton label={`Child PLU: ${childPlu}`} textToCopy={childPlu} />
            {!hidePrices ? (
              <PriceInfo price={prices?.[childPlu]} pluIsAvailable={childPluIsAvailable} />
            ) : null}
          </PriceRow>
          <PriceRow>
            <CopyButton label={`ParentChild PLU: ${parentChildPlu}`} textToCopy={parentChildPlu} />
            {!hidePrices ? (
              <PriceInfo
                price={prices?.[parentChildPlu]}
                pluIsAvailable={parentChildPluIsAvailable}
              />
            ) : null}
          </PriceRow>
        </>
      );
    case PluTypes.QUANTITY:
      let quantityPluAvailable: boolean = false;
      return (
        <>
          {(vendorConfig.quantityBasedPlu || []).map(({ plu, quantity }) => {
            quantityPluAvailable = pluIsAvailable(plu, prices);
            return (
              <>
                <PriceRow>
                  <CopyButton
                    key={plu}
                    label={`PLU: ${plu} - QUANTITY: ${quantity}`}
                    textToCopy={plu}
                  />
                  {!hidePrices ? (
                    <PriceInfo price={prices?.[plu]} pluIsAvailable={quantityPluAvailable} />
                  ) : null}
                </PriceRow>
                <Divider />
              </>
            );
          })}
        </>
      );
    case PluTypes.SIZE_BASED:
      const sizeBasedPlu = concatenateSizePlu(vendorConfig.sizeBasedPlu) ?? '';
      const sizeBasedPluIsAvailable = pluIsAvailable(sizeBasedPlu, prices);
      return (
        <PriceRow>
          <CopyButton
            label={`${vendorConfig.pluType}: ${sizeBasedPlu}`}
            textToCopy={sizeBasedPlu}
          />
          {!hidePrices ? (
            <PriceInfo price={prices?.[sizeBasedPlu]} pluIsAvailable={sizeBasedPluIsAvailable} />
          ) : null}
        </PriceRow>
      );
    default:
      return null;
  }
};

export const PluInfo = ({
  vendorConfig,
  hidePrices = false,
}: {
  vendorConfig: IVendorConfig;
  hidePrices?: boolean;
}) => {
  const { prices } = useStoreContext();

  return <PluInfoDetails vendorConfig={vendorConfig} prices={prices} hidePrices={hidePrices} />;
};
